import { usePage } from "@inertiajs/react"

import type { MenuGridCategory, MenuGridItem } from "@/components/features/menu/menu-grid"
import { StaffLayout } from "@/components/layouts/staff-layout"
import type { CancellationReasonOption } from "@/features/staff/cancellations"
import { CounterOrderWorkspace, type CounterCustomer } from "@/features/staff/counter-order-workspace"
import {
  DISCOUNTS_FEATURE,
  type DiscountKindOption,
  type DiscountReasonOption,
} from "@/features/staff/discounts"
import { REFUNDS_FEATURE, type RefundReasonOption } from "@/features/staff/refunds"
import type { ReopenReasonOption } from "@/features/staff/reopenings"
import type { TableAccountBill } from "@/features/staff/table-detail-workspace"

type OrderFormPayload = {
  categories: MenuGridCategory[]
  orphanItems: MenuGridItem[]
  ordersErrors?: Array<Record<string, string | string[] | undefined>>
}

type LocationUser = {
  role: string
} | null

type Props = {
  /** The open counter ticket, or null before the first line opens one. */
  bill: TableAccountBill | null
  customer: CounterCustomer
  orderForm: OrderFormPayload
  locationUser?: LocationUser
  refundReasons?: RefundReasonOption[]
  discountReasons?: DiscountReasonOption[]
  discountKinds?: DiscountKindOption[]
  cancellationReasons?: CancellationReasonOption[]
  reopenReasons?: ReopenReasonOption[]
  timeZone?: string
  // Shared by Staff::Location::BaseController for every screen in the domain.
  enabledFeatures?: string[]
}

// Same roster the table workspace gates on, and for the same reason: the person
// standing at the counter is who the control exists for, and a waiter's discount
// lands pending rather than taking effect. Mirrors `Location::User
// #can_apply_discounts?`.
const DISCOUNT_ROLES = ["manager", "waiter", "cashier"]

export default function StaffLocationOrdersNew() {
  const {
    bill,
    customer,
    orderForm,
    locationUser,
    refundReasons,
    discountReasons,
    discountKinds,
    cancellationReasons,
    reopenReasons,
    timeZone,
    enabledFeatures = [],
  } = usePage<Props>().props

  const isManager = locationUser?.role === "manager"
  const canRefund = isManager && enabledFeatures.includes(REFUNDS_FEATURE)
  const discountsOn = enabledFeatures.includes(DISCOUNTS_FEATURE)
  const canDiscount = discountsOn && DISCOUNT_ROLES.includes(locationUser?.role ?? "")
  const canAuthorizeDiscount = discountsOn && isManager

  return (
    <StaffLayout>
      <div className="h-full min-h-0">
        <CounterOrderWorkspace
          bill={bill}
          customer={customer}
          categories={orderForm.categories}
          orphanItems={orderForm.orphanItems}
          orderErrors={orderForm.ordersErrors}
          refundReasons={refundReasons}
          canRefund={canRefund}
          discountReasons={discountReasons}
          discountKinds={discountKinds}
          canDiscount={canDiscount}
          canAuthorizeDiscount={canAuthorizeDiscount}
          cancellationReasons={cancellationReasons}
          reopenReasons={reopenReasons}
          timeZone={timeZone}
        />
      </div>
    </StaffLayout>
  )
}
